import type { WorkspaceOut } from '@repo/api-codegen/v2';
import axios from 'axios';
import { throttle, uniqBy } from 'lodash-es';
import { authHeaders } from '../utils/authHeaders';
import { IS_PRODUCTION, IS_STORYBOOK } from '../utils/envs';
import { api } from '../utils/network';
import { captureError } from '../utils/web-tracing';
import { anonymousId } from './utils/anonymousId';
import { isImpersonating } from './utils/isImpersonating';

type AnalyticsUser = { email?: string | null };

export function analyticsPermissible(
	user?: AnalyticsUser,
	workspace?: WorkspaceOut | null
) {
	return (
		!workspace?.disable_analytics && // Ensure they haven't disable analytics in their workspace.
		user && // Ensure they are logged in.
		user.email && // This is an important check to prevent crashes.
		user.email !== 'AnonymousUser' && // Ensure they are authenticated.
		!user.email?.endsWith('mailosaur.net') && // Ensure they are not a canary (testing) user.
		IS_PRODUCTION &&
		!isImpersonating()
	);
}

interface TrackEvent {
	eventName: string;
	properties: Record<string, string>;
}

const eventsStack: TrackEvent[] = [];

/**
 * This function is used to batch events and send them to the server.
 * It is throttled to send events every 15 seconds.
 *
 * @param events - The events to send.
 * @param user - The user to send the events for.
 * @param workspace - The workspace to send the events for.
 * @returns void
 */
const flushEventsStack = throttle(
	(user?: AnalyticsUser, workspace?: WorkspaceOut | null) => {
		const events = eventsStack.splice(0, eventsStack.length);

		if (events.length > 0) {
			if (analyticsPermissible(user, workspace)) {
				// Remove duplicate events.
				const uniqueEvents = uniqBy(events, (event) => JSON.stringify(event));

				axios.post(
					`${api()}log/event_batch/`,
					{
						events: uniqueEvents,
						workspace_id: workspace?.id,
					},
					authHeaders()
				);
			} else {
				// eslint-disable-next-line no-console
				console.log('trackBatchEvents not sent:', events);
			}
		}
		// We want to send them at most every 15 seconds, letting us effectively
		// remove duplicates, and batch events.
	},
	15000
);

export function trackAnonymousEvent(
	eventName: string,
	properties: Record<string, string>,
	email?: string
) {
	if (IS_STORYBOOK) return;
	if (!email) return;

	try {
		axios.post(`${api()}log/anonymous/event/`, {
			email,
			event_name: eventName,
			properties: {
				...(properties ?? {}),
				version: import.meta.env.VITE_FRONTEND_VERSION ?? '1.0.0',
			},
		});
	} catch (e) {
		// Catch 404 errors to prevent crashes. This may happen when there is no email provided.
		captureError(e);
	}
}

export function trackBatchEvents(
	events: TrackEvent[],
	user?: AnalyticsUser,
	workspace?: WorkspaceOut | null
) {
	if (IS_STORYBOOK) return;

	const batchEventsData = events.map((event) => ({
		...event,
		event_name: event.eventName,
		properties: {
			...event.properties,
			anonymous_id: anonymousId(),
			version: import.meta.env.VITE_FRONTEND_VERSION ?? '1.0.0',
			screen_width: screen?.width?.toString(),
			screen_height: screen?.height?.toString(),
			screen_orientation: screen?.orientation?.type,
		},
	}));

	eventsStack.push(...batchEventsData);
	flushEventsStack(user, workspace);
}

export function trackEvent(
	eventName: string,
	properties: Record<string, string>,
	user?: AnalyticsUser,
	workspace?: WorkspaceOut | null
) {
	trackBatchEvents([{ eventName, properties }], user, workspace);
}
